import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { array, boolean, number, object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { isPresent } from '@jjvgaming/player-payback-library'
import { isNil } from 'lodash'
import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { Modal } from 'components/Modal/Modal'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage/FormFieldErrorMessage'
import { ActivityButton } from 'components/ActivityButton'
import { SectionHeader } from 'components/SectionHeader'
import { SweepstakesDetailsHeader } from './SweepstakeDetailsHeader'
import { useGetSweepstakeById } from 'hooks/api/useGetSweepstakeById'
import { SweepstakesDetailsSection } from './SweepstakeDetailsSection'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { SweepstakeImageSection } from './SweepstakeImageSection'
import { SweepstakeParticipatingGuidelineSection } from './SweepstakeParticipatingGuidelineSection'
import { usePutSweepstake } from 'hooks/api/Sweepstake/usePutSweepstake'
import { useGetTiers } from 'hooks/api/useGetTiers'
import { useLayout } from 'hooks/useLayout'
import { useSnackbar } from 'stores/useSnackbar'
import { type Sweepstake, type Tier } from 'types/api'

const blankGuid = '00000000-0000-0000-0000-000000000000'

type ContestWinnerWithTier = NonNullable<
  NonNullable<Sweepstake['contestWinners']>[number]
> & {
  tierId?: number | null
}

type TierWithRequiredId = Tier & {
  id: number
  type: string
}

interface PrizeFormWinner {
  winnerNumber?: number
  cashPrize?: boolean | null
  prizeType?: string
  prizeAmount?: number
  prizeDescription: string
}

interface PrizeFormValues {
  Sweepstake: {
    ContestWinner?: PrizeFormWinner[]
  }
}

const sweepstakePrizeSchema = object().shape({
  Sweepstake: object({
    ContestWinner: array().of(
      object({
        winnerNumber: number().required(),
        cashPrize: boolean().nullable(),
        prizeType: string(),
        prizeAmount: number()
          .typeError('Prize Amount must be a number 0 or greater')
          .when('cashPrize', {
            is: true,
            then: (schema) =>
              schema
                .min(0, 'Prize Amount must be 0 or greater')
                .max(100000, 'Prize Amount cannot exceed 100,000'),
            otherwise: (schema) => schema.optional(),
          }),
        prizeDescription: string().required(
          'Prize Description is a required field'
        ),
      })
    ),
  }),
})

const getPrizeType = (prizeType?: string | null) => {
  return prizeType === 'Prize' || prizeType === 'Cash' || prizeType === 'Email'
    ? prizeType
    : 'Prize'
}

const SweepstakePrizeSections = ({
  sweepstake,
}: {
  sweepstake: Sweepstake
}) => {
  const { isMobile } = useLayout()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedPrizeTarget, setSelectedPrizeTarget] = useState<
    TierWithRequiredId | 'location' | null
  >(null)

  const tiersQuery = useGetTiers()
  const putSweepstakeMutation = usePutSweepstake({
    sweepstakeId: sweepstake.id,
    onSuccess: () => {
      setMessage('Sweepstake prizes have been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      setIsModalOpen(false)
      setSelectedPrizeTarget(null)
    },
    onError: () => {
      setMessage('Error updating sweepstake prizes', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const numGrandPrizeWinners =
    sweepstake.sweepstakeData?.numGrandPrizeWinnersPerTier ?? 1
  const isGrandPrizeWithLocation =
    sweepstake.grandPrizeType === 'GrandPrizeWithLocation'
  const contestWinners =
    (sweepstake.contestWinners as ContestWinnerWithTier[] | undefined) ?? []

  const buildPrizeRows = (tierId: number | null, rowCount: number) => {
    const matchingContestWinners = contestWinners
      .filter((winner) => winner.tierId === tierId)
      .sort((a, b) => (a.winnerNumber ?? 0) - (b.winnerNumber ?? 0))

    return Array.from({ length: rowCount }, (_, index) => {
      const winner = matchingContestWinners[index]
      return {
        winnerNumber: winner?.winnerNumber ?? index + 1,
        cashPrize: winner?.cashPrize ?? false,
        prizeType: getPrizeType(winner?.prizeType),
        prizeAmount: Number(winner?.prizeAmount ?? 0),
        prizeDescription: winner?.prizeDescription ?? '',
      }
    })
  }

  const formMethods = useForm<PrizeFormValues>({
    resolver: yupResolver<PrizeFormValues>(sweepstakePrizeSchema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        ContestWinner: [],
      },
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  if (tiersQuery.isPending) {
    return <ActivityIndicator />
  }

  if (tiersQuery.isError || !tiersQuery.data) {
    return <Box>An error occurred fetching the tiers.</Box>
  }

  const tiers = (tiersQuery.data as TierWithRequiredId[])
    .filter((tier) => isPresent(tier.id) && isPresent(tier.type))
    .sort((a, b) => a.id - b.id)

  const handleOpenTierModal = (tier: TierWithRequiredId) => {
    setSelectedPrizeTarget(tier)
    reset({
      Sweepstake: {
        ContestWinner: buildPrizeRows(tier.id, numGrandPrizeWinners),
      },
    })
    setIsModalOpen(true)
  }

  const handleOpenLocationPrizeModal = () => {
    if (!isGrandPrizeWithLocation) {
      return
    }

    setSelectedPrizeTarget('location')
    reset({
      Sweepstake: {
        ContestWinner: buildPrizeRows(null, 1),
      },
    })
    setIsModalOpen(true)
  }

  const handleCloseTierModal = () => {
    setIsModalOpen(false)
    setSelectedPrizeTarget(null)
  }

  const handleSubmitWrapper = handleSubmit((data) => {
    if (!selectedPrizeTarget) {
      return
    }

    if (selectedPrizeTarget === 'location' && !isGrandPrizeWithLocation) {
      return
    }

    const selectedTierId =
      selectedPrizeTarget === 'location' ? null : selectedPrizeTarget.id

    const preservedContestWinners: ContestWinnerWithTier[] = contestWinners
      .filter((winner) => winner.tierId !== selectedTierId)
      .map((winner) => ({
        groupId: winner.groupId ?? sweepstake.groupId ?? blankGuid,
        contestId: winner.contestId ?? sweepstake.id ?? 0,
        winnerNumber: winner.winnerNumber ?? 0,
        cashPrize: getPrizeType(winner.prizeType) === 'Cash',
        prizeType: getPrizeType(winner.prizeType),
        prizeAmount: Number(winner.prizeAmount ?? 0),
        prizeDescription: winner.prizeDescription ?? '',
        tierId: winner.tierId ?? null,
        id: winner.id,
      }))

    const updatedTierContestWinners: ContestWinnerWithTier[] = (
      data.Sweepstake.ContestWinner ?? []
    ).map((winner: PrizeFormWinner, index: number) => {
      const winnerNumber = Number(winner.winnerNumber ?? index + 1)
      const existingWinner = contestWinners.find(
        (contestWinner) =>
          contestWinner.tierId === selectedTierId &&
          contestWinner.winnerNumber === winnerNumber
      )

      return {
        groupId: existingWinner?.groupId ?? sweepstake.groupId ?? blankGuid,
        contestId: sweepstake.id ?? 0,
        winnerNumber,
        cashPrize: getPrizeType(winner.prizeType) === 'Cash',
        prizeType: getPrizeType(winner.prizeType),
        prizeAmount: Number(winner.prizeAmount ?? 0),
        prizeDescription: winner.prizeDescription ?? '',
        tierId: selectedTierId,
        id: existingWinner?.id ?? 0,
      }
    })

    putSweepstakeMutation.mutate({
      ...sweepstake,
      contestWinners: [
        ...preservedContestWinners,
        ...updatedTierContestWinners,
      ],
    })
  })

  const isLocationPrizeSelected = selectedPrizeTarget === 'location'
  let modalTitle = ''
  let modalRowCount = numGrandPrizeWinners

  if (isLocationPrizeSelected) {
    modalTitle = 'Location Prize'
    modalRowCount = 1
  }

  if (selectedPrizeTarget && selectedPrizeTarget !== 'location') {
    modalTitle = `${selectedPrizeTarget.type} Prizes`
  }

  return (
    <>
      {tiers.map((tier) => {
        const tierPrizeRows = buildPrizeRows(tier.id, numGrandPrizeWinners)
        const hasPrizeRows = tierPrizeRows.some(
          (winner) => winner.prizeDescription || Number(winner.prizeAmount) > 0
        )

        return (
          <Box key={tier.id}>
            <SectionHeader
              title={`${tier.type} Prizes`}
              onClickButton={() => handleOpenTierModal(tier)}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                rowGap: '16px',
                borderWidth: 1,
                borderColor: 'grey.300',
                borderRadius: 2,
                p: '16px',
                mb: '40px',
              }}
            >
              {hasPrizeRows ? null : <Typography>No prizes</Typography>}

              <Grid container spacing={3}>
                {hasPrizeRows &&
                  tierPrizeRows.map((winner, index) => (
                    <Grid
                      item
                      xs={12}
                      key={`${tier.id}-${winner.winnerNumber}`}
                    >
                      <Typography variant="body1">
                        <strong>Winner #{index + 1}: </strong>
                        {winner.prizeDescription}
                        {Number(winner.prizeAmount) > 0
                          ? ` - $${Number(winner.prizeAmount).toFixed(2)}`
                          : ''}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          </Box>
        )
      })}

      {isGrandPrizeWithLocation
        ? (() => {
            const locationPrizeRows = buildPrizeRows(null, 1)
            const hasLocationPrize = locationPrizeRows.some(
              (winner) =>
                winner.prizeDescription || Number(winner.prizeAmount) > 0
            )

            return (
              <Box key="location-prize">
                <SectionHeader
                  title="Location Prize"
                  onClickButton={handleOpenLocationPrizeModal}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    rowGap: '16px',
                    borderWidth: 1,
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: '16px',
                    mb: '40px',
                  }}
                >
                  {hasLocationPrize ? null : <Typography>No prize</Typography>}

                  <Grid container spacing={3}>
                    {hasLocationPrize &&
                      locationPrizeRows.map((winner) => (
                        <Grid
                          item
                          xs={12}
                          key={`location-${winner.winnerNumber}`}
                        >
                          <Typography variant="body1">
                            {winner.prizeDescription}
                            {Number(winner.prizeAmount) > 0
                              ? ` - $${Number(winner.prizeAmount).toFixed(2)}`
                              : ''}
                          </Typography>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              </Box>
            )
          })()
        : null}

      <Modal
        isOpen={isModalOpen}
        toggleIsOpen={() => {}}
        sx={{
          width: isMobile ? '100%' : '50%',
          height: isMobile ? '100%' : '55%',
        }}
      >
        <Box>
          <form onSubmit={handleSubmitWrapper}>
            <ModalFullHeader
              toggleIsOpen={handleCloseTierModal}
              title={modalTitle}
            />
            <ModalFullBody heightModal={isMobile ? '100%' : '55%'}>
              {Array.from({ length: modalRowCount }, (_, idx) => idx + 1).map(
                (winnerNumber) => (
                  <Grid
                    container
                    spacing={2}
                    marginTop={1}
                    borderTop={1}
                    key={winnerNumber}
                  >
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 1 }}>
                        {isLocationPrizeSelected
                          ? 'Location Prize'
                          : `Winner #${winnerNumber}`}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Type
                      </InputLabel>
                      <Select
                        style={{ height: '40px' }}
                        placeholder="Prize"
                        {...register(
                          `Sweepstake.ContestWinner.${winnerNumber - 1}.prizeType`
                        )}
                        defaultValue={
                          formMethods.getValues(
                            `Sweepstake.ContestWinner.${winnerNumber - 1}.prizeType`
                          ) ?? 'Prize'
                        }
                      >
                        <MenuItem value="Prize">Prize</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Description
                      </InputLabel>
                      <TextField
                        fullWidth
                        {...register(
                          `Sweepstake.ContestWinner.${winnerNumber - 1}.prizeDescription`
                        )}
                        error={isPresent(
                          errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                            ?.prizeDescription
                        )}
                        size="small"
                        inputProps={{ maxLength: 50 }}
                      />
                      {errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                        ?.prizeDescription?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                              ?.prizeDescription?.message as string
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Amount
                      </InputLabel>
                      <TextField
                        type="number"
                        {...register(
                          `Sweepstake.ContestWinner.${winnerNumber - 1}.prizeAmount`
                        )}
                        error={isPresent(
                          errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                            ?.prizeAmount
                        )}
                        size="small"
                        inputProps={{ min: 0, max: 100000 }}
                        sx={{ width: '100%' }}
                      />
                      {errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                        ?.prizeAmount?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.ContestWinner?.[winnerNumber - 1]
                              ?.prizeAmount?.message as string
                          }
                        />
                      )}
                    </Grid>
                  </Grid>
                )
              )}
            </ModalFullBody>
            <ModalFullFooter>
              <Button onClick={handleCloseTierModal} sx={{ marginRight: 1 }}>
                Cancel
              </Button>
              <ActivityButton
                type="submit"
                variant="contained"
                active={putSweepstakeMutation.isPending}
              >
                Save
              </ActivityButton>
            </ModalFullFooter>
          </form>
        </Box>
      </Modal>
    </>
  )
}

export const SweepstakeOverviewPage = () => {
  const { id: idParam } = useParams()
  const sweepstakeId = Number(idParam)
  const sweepstakeQuery = useGetSweepstakeById(sweepstakeId)

  if (sweepstakeQuery.isPending) {
    return <ActivityIndicator />
  }

  if (sweepstakeQuery.isError || isNil(sweepstakeQuery.data)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  const sweepstake = sweepstakeQuery.data

  return (
    <SweepstakesDetailsHeader
      sweepstake={sweepstake}
      currentTab={`/Contests/Sweepstakes/${sweepstake.id}/Overview`}
    >
      <Box>
        <SweepstakesDetailsSection sweepstake={sweepstake} />
        <SweepstakeImageSection sweepstake={sweepstake} />
        <SweepstakePrizeSections sweepstake={sweepstake} />
        <SweepstakeParticipatingGuidelineSection sweepstake={sweepstake} />
      </Box>
    </SweepstakesDetailsHeader>
  )
}
